import { useState } from 'react';
import StepIntent from './StepIntent';
import StepSpecificity from './StepSpecificity';
import StepListingInput from './StepListingInput';
import StepGeneralInput from './StepGeneralInput';
import AnalysisResults from './AnalysisResults';
import RepairQuoteInput from './RepairQuoteInput';
import RepairVehicleSelect from './RepairVehicleSelect';
import RepairResults from './RepairResults';
import { saveAutoAdvisorAnalysis, removeAutoAdvisorAnalysis } from '../../utils/storage';

const PURCHASE_VISIBLE_STEPS = 3;
const REPAIR_VISIBLE_STEPS = 2;

const INITIAL_DATA = {
  intent: '',
  hasSpecificListing: null,
  listingUrl: '',
  listingPhotos: [],
  askingPrice: '',
  year: '',
  make: '',
  model: '',
  trim: '',
  mileage: '',
  condition: '',
  intendedUse: '',
  budgetMin: '',
  budgetMax: '',
  results: null,
  // Repair fields
  repairQuotePhoto: null,
  repairShopName: '',
  repairDescription: '',
  repairTotalQuoted: '',
  repairLineItems: [{ description: '', cost: '' }],
  repairVehicleYear: '',
  repairVehicleMake: '',
  repairVehicleModel: '',
  repairVehicleId: null,
  repairMileage: '',
  repairZipCode: '',
};

export default function AutoAdvisor({ onBack, vehicles, initMode }) {
  const [advisorMode, setAdvisorMode] = useState(initMode || null); // null | 'purchase' | 'repair'
  const [step, setStep] = useState(initMode ? 1 : 0); // 0 = mode selector, skip if initMode
  const [data, setData] = useState({ ...INITIAL_DATA });

  function update(fields) {
    setData((prev) => ({ ...prev, ...fields }));
  }

  function next() {
    setStep((s) => s + 1);
  }

  function back() {
    if (step === 0) {
      onBack();
      return;
    }
    if (step === 1 && advisorMode) {
      // Going back from first step of either mode → return to mode selector
      setAdvisorMode(null);
      setStep(0);
      return;
    }
    setStep((s) => s - 1);
  }

  function handleSelectMode(mode) {
    setAdvisorMode(mode);
    setStep(1);
  }

  // Called from the starter pages of either flow when the user taps
  // "View saved reports". The saved reports list lives on step 0 (the
  // mode selector), so we just rewind there. We don't blow away any
  // half-entered data since the user can come back via the cards.
  function jumpToSavedReports() {
    setAdvisorMode(null);
    setStep(0);
  }

  function saveResults(results) {
    update({ results });
    saveAutoAdvisorAnalysis({ ...data, results });
  }

  function reset() {
    setAdvisorMode(null);
    setStep(0);
    setData({ ...INITIAL_DATA });
  }

  function viewSavedReport(analysis) {
    // Load the saved analysis data and jump to the results step
    const isRepair = !!analysis.repairDescription || !!analysis.repairQuotePhoto;
    const newData = { ...INITIAL_DATA, ...analysis };
    setData(newData);
    setAdvisorMode(isRepair ? 'repair' : 'purchase');
    setStep(isRepair ? 3 : 4); // Jump straight to results
  }

  function refreshSavedReport(analysis) {
    // Load the saved analysis input data and re-run the analysis
    const isRepair = !!analysis.repairDescription || !!analysis.repairQuotePhoto;
    const newData = { ...INITIAL_DATA, ...analysis, results: null }; // Clear old results
    setData(newData);
    setAdvisorMode(isRepair ? 'repair' : 'purchase');
    setStep(isRepair ? 3 : 4); // Go to results step which will re-run analysis
  }

  function handleRefreshCurrent() {
    // Re-run analysis for the currently viewed saved report
    const isRepair = !!data.repairDescription || !!data.repairQuotePhoto;
    setData((prev) => ({ ...prev, results: null }));
    // Force re-mount of AnalysisResults by toggling step
    setStep(0);
    setTimeout(() => {
      setAdvisorMode(isRepair ? 'repair' : 'purchase');
      setStep(isRepair ? 3 : 4);
    }, 0);
  }

  function handleDeleteCurrent() {
    if (data.savedAt) {
      removeAutoAdvisorAnalysis(data.savedAt);
    }
    reset();
  }

  /* ─── Purchase mode step mapping ─── */
  // Step 0: Mode selector (shared)
  // Step 1: Intent selection
  // Step 2: Specificity
  // Step 3: Listing/General input
  // Step 4: Analysis results

  /* ─── Repair mode step mapping ─── */
  // Step 0: Mode selector (shared)
  // Step 1: Quote input
  // Step 2: Vehicle selection
  // Step 3: Results

  const isPurchaseMode = advisorMode === 'purchase';
  const isRepairMode = advisorMode === 'repair';

  const visibleSteps = isPurchaseMode ? PURCHASE_VISIBLE_STEPS : REPAIR_VISIBLE_STEPS;
  const visibleStep = Math.min(step, visibleSteps);
  const showProgress = (isPurchaseMode && step >= 1 && step <= 3) || (isRepairMode && step >= 1 && step <= 2);

  return (
    <div className="screen" style={{ paddingBottom: 100, overflowY: 'auto' }}>
      {/* Progress bar — only during input steps */}
      {showProgress && (
        <div className="progress-bar">
          {Array.from({ length: visibleSteps }, (_, i) => (
            <div
              key={i}
              className={`progress-step ${
                i + 1 === visibleStep
                  ? 'active'
                  : i + 1 < visibleStep
                    ? 'done'
                    : ''
              }`}
            />
          ))}
        </div>
      )}

      {/* Mode selector */}
      {step === 0 && (
        <StepIntent
          data={data}
          update={update}
          onNext={next}
          onBack={onBack}
          advisorMode={null}
          onSelectMode={handleSelectMode}
          onViewSaved={viewSavedReport}
          onRefreshSaved={refreshSavedReport}
        />
      )}

      {/* ─── Purchase flow ─── */}
      {isPurchaseMode && step === 1 && (
        <StepIntent
          data={data}
          update={update}
          onNext={next}
          onBack={back}
          advisorMode="purchase"
          onSelectMode={handleSelectMode}
          onViewSaved={jumpToSavedReports}
        />
      )}
      {isPurchaseMode && step === 2 && (
        <StepSpecificity
          data={data}
          update={update}
          onNext={next}
          onBack={back}
        />
      )}
      {isPurchaseMode && step === 3 && data.hasSpecificListing && (
        <StepListingInput
          data={data}
          update={update}
          onNext={next}
          onBack={back}
        />
      )}
      {isPurchaseMode && step === 3 && data.hasSpecificListing === false && (
        <StepGeneralInput
          data={data}
          update={update}
          onNext={next}
          onBack={back}
        />
      )}
      {isPurchaseMode && step === 4 && (
        <AnalysisResults
          data={data}
          onSave={saveResults}
          onBack={back}
          onReset={reset}
          onExit={onBack}
          onRefresh={data.savedAt ? handleRefreshCurrent : null}
          onDelete={data.savedAt ? handleDeleteCurrent : null}
        />
      )}

      {/* ─── Repair flow ─── */}
      {isRepairMode && step === 1 && (
        <RepairQuoteInput
          data={data}
          update={update}
          onNext={next}
          onBack={back}
          onViewSaved={jumpToSavedReports}
        />
      )}
      {isRepairMode && step === 2 && (
        <RepairVehicleSelect
          data={data}
          update={update}
          onNext={next}
          onBack={back}
          vehicles={vehicles || []}
        />
      )}
      {isRepairMode && step === 3 && (
        <RepairResults
          data={data}
          onReset={reset}
          onExit={onBack}
        />
      )}
    </div>
  );
}
